import React, { useRef, useState, useCallback, memo, useEffect, useMemo } from 'react';
import { OptionType } from '../bx-select.types';
import { CMField, CMTable } from '../../../Pages/Library/CustomModule/Library/add-edit.types';
import {
  DropdownConfigValue,
  CheckboxConfigValue,
  RadioConfigValue,
  TextBoxConfigValue,
  DatePickerConfigValue,
  TableColumnsEditorProps,
  StatusConfigValue,
  SourceConfigValue,
  ImageConfigValue,
  CalculationConfigValue,
  FieldItem,
} from './types';
import { DropdownConfig, CheckboxConfig, RadioConfig, TextBoxConfig, DatePickerConfig, StatusConfig, SourceConfig, ImageConfig, CalculationConfig } from '.';
import ConfirmPopup from '../../ModalPopup/confirm-popup';
import LibraryTitleField from '../library-title-field';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../Toaster/Toaster';
import { MAX_TABLE_COLUMNS_LIMIT, MAX_FROZEN_COLUMNS_LIMIT } from '../../../Pages/Library/CustomModule/Library/utils/constants';
import { CloseIcon, DeleteIcon, EditIcon, HideIcon, RearrangeIcon, ShowIcon } from '../../../bombiix-icon';
import { NewInput } from '../../PIM/common/NewInput';
import { NewSelectMultiSelect } from '../../PIM/common/NewSelectMultiSelect';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import FieldSetupCard from '../../../Pages/Library/CustomModule/Library/components/field-setup-card';
import { getFieldSetupAccess } from '../../../Pages/Library/CustomModule/Library/components/field-setup-access';

/**
 * Available column type options for table columns
 */
const typeOptions: OptionType[] = [
  { label: 'Dropdown', value: 'dorpdown' },
  { label: 'Text Box', value: 'text' },
  { label: 'Radio Button', value: 'radio' },
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Date Picker', value: 'date-picker' },
  { label: 'Image', value: 'image' },
  { label: 'Status', value: 'status' },
  { label: 'Source', value: 'source' },
  { label: 'Calculation', value: 'calculation' },
];

/**
 * Converts a field type to its human-readable label
 * @param kind - The field type to convert
 * @returns Human-readable label for the field type
 */
const typeLabel = (kind: CMField['type']): string => {
  switch (kind) {
    case 'dorpdown':
      return 'Dropdown';
    case 'text':
      return 'Text Box';
    case 'radio':
      return 'Radio Button';
    case 'checkbox':
      return 'Checkbox';
    case 'date-picker':
      return 'Date Picker';
    case 'image':
      return 'Image';
    case 'status':
      return 'Status';
    case 'source':
      return 'Source';
    case 'calculation':
      return 'Calculation';
    default:
      return kind;
  }
};

/**
 * Creates a drag image for the given text
 * @param text - Text to display in the drag image
 * @returns HTMLImageElement or null if creation fails
 */
const createDragImage = (text: string): HTMLImageElement | null => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.font = '14px system-ui, -apple-system, sans-serif';
    const metrics = ctx.measureText(text);
    const padding = 12;
    const width = Math.ceil(metrics.width) + padding * 2;
    const height = 32;

    canvas.width = width;
    canvas.height = height;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = '#007bff';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, width - 1, height - 1);

    ctx.fillStyle = '#000000';
    ctx.font = '14px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, width / 2, height / 2);

    const img = new Image();
    img.src = canvas.toDataURL();
    return img;
  } catch {
    return null;
  }
};

/**
 * Empty image for hiding default drag preview
 */
const EMPTY_IMAGE = (() => {
  const img = new Image();
  img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
  return img;
})();

/**
 * TableColumnsEditor component for configuring table columns in the system canvas.
 * Allows users to add, edit, remove, and reorder table columns with different control types.
 * @param table - Current table configuration
 * @param setTable - Function to update table configuration
 * @param subIndex - Sub-index for nested table paths
 * @param editingTablePath - Current editing path for column configuration
 * @param setEditingTablePath - Function to set the editing path
 * @param toFieldItem - Function to convert field to item format
 * @param applyConfigToField - Function to apply configuration to field
 * @param showLibraryTitle - Whether to show the library title input section
 */
const TableColumnsEditor: React.FC<TableColumnsEditorProps> = ({
  table,
  setTable,
  subIndex,
  editingTablePath,
  setEditingTablePath,
  toFieldItem,
  applyConfigToField,
  showLibraryTitle = true,
}) => {
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const { t } = useTranslation('custom-module-system-canvas');
  const { t: tCommon } = useTranslation('common');
  const dragImageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

  const frozenColumnsCount = useMemo(() => table.columns.filter((col) => col.isFrozen === true).length, [table.columns]);
  const isFrozenLimitReached = frozenColumnsCount >= MAX_FROZEN_COLUMNS_LIMIT;

  useEffect(() => {
    const cache = dragImageCacheRef.current;
    const columnTexts = new Set(table.columns.map((col, idx) => col.title || `Column ${idx + 1}`));

    columnTexts.forEach((text) => {
      if (!cache.has(text)) {
        const img = createDragImage(text);
        if (img) {
          cache.set(text, img);
        }
      }
    });

    cache.forEach((_, key) => {
      if (!columnTexts.has(key)) {
        cache.delete(key);
      }
    });
  }, [table.columns]);

  const handleDragStart = useCallback((e: React.DragEvent, colIdx: number, columnText: string) => {
    dragIndexRef.current = colIdx;

    try {
      e.dataTransfer.setData('text/plain', String(colIdx));
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setDragImage(EMPTY_IMAGE, 0, 0);

      const dragImg = dragImageCacheRef.current.get(columnText);

      if (dragImg?.complete) {
        e.dataTransfer.setDragImage(dragImg, dragImg.width / 2, dragImg.height / 2);
      } else {
        const newImg = createDragImage(columnText);
        if (newImg) {
          dragImageCacheRef.current.set(columnText, newImg);
          if (newImg.complete) {
            e.dataTransfer.setDragImage(newImg, newImg.width / 2, newImg.height / 2);
          } else {
            newImg.onload = () => {
              e.dataTransfer.setDragImage(newImg, newImg.width / 2, newImg.height / 2);
            };
          }
        }
      }
    } catch {
      e.dataTransfer.setDragImage(EMPTY_IMAGE, 0, 0);
    }
  }, []);

  /**
   * Adds a new column of the specified type to the table
   * @param kind - The type of column to add
   */
  const addColumn = useCallback(
    (kind: CMField['type']) => {
      setTable((currentTable: CMTable) => {
        const cols: CMField[] = Array.isArray(currentTable.columns) ? [...currentTable.columns] : [];

        if (cols.length >= MAX_TABLE_COLUMNS_LIMIT) {
          toasterMsg(t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_COLUMNS.MAX_COLUMNS_REACHED'), 'danger');
          return currentTable;
        }

        const base: CMField = {
          type: kind,
          isRequired: false,
          title: '',
          value: null,
          isMultiple: false,
          options: ['Option 1'],
          range: false,
          isVisible: true,
        };
        if (!['dorpdown', 'checkbox', 'radio'].includes(kind)) delete (base as Partial<Pick<CMField, 'options'>>).options;
        if (kind !== 'dorpdown') delete (base as Partial<Pick<CMField, 'isMultiple'>>).isMultiple;

        if (kind === 'status') {
          base.statusColours = [
            { statusName: t('COMMON.STATUS_COLORS.PENDING'), statusHexCode: '#FDD499', setDefault: true },
            { statusName: t('COMMON.STATUS_COLORS.APPROVED'), statusHexCode: '#73C5AF' },
            { statusName: t('COMMON.STATUS_COLORS.REJECTED'), statusHexCode: '#FA979B' },
            { statusName: t('COMMON.STATUS_COLORS.APPROVED_WITH_COMMENTS'), statusHexCode: '#DCF0EB' },
          ];
        }

        return {
          ...currentTable,
          columns: [...cols, base],
        };
      });
    },
    [setTable, t],
  );

  /**
   * Updates a column at the specified index with partial field data
   * @param index - Index of the column to update
   * @param patch - Partial field data to apply
   */
  const updateCol = useCallback(
    (index: number, patch: Partial<CMField>) => {
      setTable((currentTable: CMTable) => {
        const cols: CMField[] = Array.isArray(currentTable.columns) ? [...currentTable.columns] : [];

        if (patch.isFrozen === true) {
          const currentColumn = cols[index];
          if (currentColumn && currentColumn.isFrozen !== true) {
            const currentFrozenCount = cols.filter((col) => col.isFrozen === true).length;
            if (currentFrozenCount >= MAX_FROZEN_COLUMNS_LIMIT) {
              toasterMsg(t('CUSTOM_MODULE.VALIDATION.MAX_FROZEN_COLUMNS_LIMIT'), 'danger');
              return currentTable;
            }
          }
        }

        const updatedCols = cols.map((c: CMField, i: number) => (i === index ? { ...c, ...patch } : c));

        if (patch.isFrozen !== undefined) {
          const frozenCols: CMField[] = [];
          const nonFrozenCols: CMField[] = [];

          updatedCols.forEach((col) => {
            if (col.isFrozen === true) {
              frozenCols.push(col);
            } else {
              nonFrozenCols.push(col);
            }
          });

          return {
            ...currentTable,
            columns: [...frozenCols, ...nonFrozenCols],
          };
        }

        return {
          ...currentTable,
          columns: updatedCols,
        };
      });
    },
    [setTable, t],
  );

  /**
   * Removes a column at the specified index from the table
   * @param index - Index of the column to remove
   */
  const removeCol = useCallback(
    (index: number) =>
      setTable((currentTable: CMTable) => {
        const cols: CMField[] = Array.isArray(currentTable.columns) ? [...currentTable.columns] : [];
        return {
          ...currentTable,
          columns: cols.filter((_: CMField, i: number) => i !== index),
        };
      }),
    [setTable],
  );

  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  /**
   * Initiates the column removal confirmation process
   * @param index - Index of the column to be removed
   */
  const requestRemoveCol = useCallback(
    (index: number) => {
      if (showDeleteConfirm) return;
      setConfirmDeleteIndex(index);
      setShowDeleteConfirm(true);
    },
    [showDeleteConfirm],
  );

  /**
   * Confirms and executes the removal of a table column
   */
  const handleConfirmDelete = useCallback(() => {
    if (confirmDeleteIndex !== null) {
      removeCol(confirmDeleteIndex);
    }
    setShowDeleteConfirm(false);
    setConfirmDeleteIndex(null);
  }, [confirmDeleteIndex, removeCol]);

  /**
   * Cancels the column removal confirmation
   */
  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setConfirmDeleteIndex(null);
  }, []);

  const [addType, setAddType] = useState<OptionType | null>(null);

  /**
   * Handles selection of column type from the dropdown to add a new column
   * @param option - Selected option containing the column type
   */
  const onAddColumnSelect = useCallback(
    (option: OptionType | null) => {
      setAddType(option);
      if (option?.value) {
        addColumn(option.value as CMField['type']);
        setAddType(null);
      }
    },
    [addColumn],
  );

  /**
   * Updates the table title
   * @param next - New title value
   */
  const updateTableTitle = useCallback(
    (next: string) => {
      setTable((currentTable: CMTable) => ({
        ...currentTable,
        title: next,
      }));
    },
    [setTable],
  );

  const handleLibrarySelect = useCallback(
    (pulled: unknown) => {
      const fetchedTable = pulled as CMTable | undefined;
      if (!fetchedTable) return;
      const importedColumns = Array.isArray(fetchedTable.columns) ? fetchedTable.columns.slice(0, MAX_TABLE_COLUMNS_LIMIT) : [];
      setTable(() => ({
        type: 'table' as const,
        title: fetchedTable.title || '',
        ...(fetchedTable.isAccordion !== undefined && { isAccordion: fetchedTable.isAccordion }),
        columns: importedColumns,
        _importedFromLibrary: true,
      }));
    },
    [setTable],
  );

  /**
   * Renders a column's type-specific config inside the field setup card, as section fields do.
   * @param item - The column as a field item.
   * @param column - The column being edited.
   * @param colIndex - Index of the column in the table.
   * @returns The config component, or null for an unsupported type.
   */
  const renderColumnConfig = (item: FieldItem, column: CMField, colIndex: number) => {
    const apply = (nextConfig: unknown) => updateCol(colIndex, applyConfigToField(column, nextConfig));
    switch (item.type) {
      case 'dropdown':
        return <DropdownConfig value={item.config as DropdownConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'checkbox':
        return <CheckboxConfig value={item.config as CheckboxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'radio':
        return <RadioConfig value={item.config as RadioConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'text':
        return <TextBoxConfig value={item.config as TextBoxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'date':
        return <DatePickerConfig value={item.config as DatePickerConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'status':
        return <StatusConfig value={item.config as StatusConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'source':
        return <SourceConfig value={item.config as SourceConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'image':
        return <ImageConfig value={item.config as ImageConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'calculation':
        return <CalculationConfig value={item.config as CalculationConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      default:
        return null;
    }
  };

  return (
    <div className="cm-maxw-520 mt-2 mb-3">
      <div className="d-flex align-items-center">
        <div className="cm-checkbox my-2">
          <label className="cursor-pointer">Convert to Accordion</label>
          <NewCheckbox className="scale12" checked={!!table?.isAccordion} onChange={(e) => setTable((currentTable) => ({ ...currentTable, isAccordion: e.target.checked }))} />
        </div>
      </div>
      <div className="mb-2">
        <p className="m-0">Table Title</p>
        {showLibraryTitle ? (
          <LibraryTitleField
            value={table?.title || ''}
            onChange={updateTableTitle}
            onPull={handleLibrarySelect}
            controlType="table"
            content={table}
            placeholder="Enter"
          />
        ) : (
          <NewInput type="text" placeholder="Enter" value={table?.title || ''} onChange={(e) => updateTableTitle(e.target.value)} />
        )}
      </div>
      <div className="mb-2">
        <div className="d-flex align-items-center justify-content-between">
          <p className="m-0">Add Column</p>
          {table.columns.length >= MAX_TABLE_COLUMNS_LIMIT && (
            <p className="m-0 f-12 text-danger">{t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_COLUMNS.MAX_COLUMNS_MESSAGE')}</p>
          )}
        </div>
        <NewSelectMultiSelect
          menuPortalTarget={document.body}
          menuPosition="fixed"
          options={typeOptions}
          value={addType}
          onChange={onAddColumnSelect}
          placeholder="Select"
          isDisabled={table.columns.length >= MAX_TABLE_COLUMNS_LIMIT}
        />
      </div>
      <div className="mb-1">
        <div className="">
          <div className="d-flex px-2 py-1 border bg-f3f3f3">
            <div className="w-100px flex-grow-1 f-12">Column Title</div>
            <div className="w-80px f-12">Type</div>
            <div className="w-70px f-12 text-center">
              {t('CUSTOM_MODULE.SYSTEM_CANVAS.FREEZE')}
              {
                <span className="text-danger ml-1" title={t('CUSTOM_MODULE.VALIDATION.MAX_FROZEN_COLUMNS_LIMIT')}>
                  ({frozenColumnsCount}/{MAX_FROZEN_COLUMNS_LIMIT})
                </span>
              }
            </div>
            <div className="w-50px f-12 text-center">Actions</div>
          </div>
          {table.columns.map((col: CMField, colIdx: number) => (
            <div
              key={`col_${colIdx}`}
              className={`d-flex align-items-center px-2 py-1 border-bottom cursor-move ${dragOverIndex === colIdx ? 'bg-f3f3f3' : ''} ${
                editingTablePath?.colIndex === colIdx ? 'bg-1e90ff40' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, colIdx, col.title || `Column ${colIdx + 1}`)}
              onDragEnter={() => {
                if (dragOverIndex !== colIdx) setDragOverIndex(colIdx);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragOverIndex !== colIdx) setDragOverIndex(colIdx);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const from = dragIndexRef.current;
                const to = colIdx;
                if (from !== null && to !== null && from !== to) {
                  setTable((currentTable: CMTable) => {
                    const cols: CMField[] = Array.isArray(currentTable.columns) ? [...currentTable.columns] : [];
                    const item = cols[from];
                    if (!item) return currentTable;
                    cols.splice(from, 1);
                    cols.splice(to, 0, item);
                    return { ...currentTable, columns: cols };
                  });
                }
                dragIndexRef.current = null;
                setDragOverIndex(null);
              }}
              onDragEnd={() => {
                dragIndexRef.current = null;
                setDragOverIndex(null);
              }}
            >
              <div className="flex-grow-1 pr-2">
                <div className="d-flex align-items-center w-90">
                  <RearrangeIcon size={24} className="flex-shrink-0 mr-2" style={{ cursor: 'move' }} />
                  <NewInput placeholder="Enter" value={col.title || ''} onChange={(e) => updateCol(colIdx, { title: e.target.value })} />
                </div>
              </div>
              <div className="w-100px f-12 d-flex align-items-center">{typeLabel(col.type)}</div>

              <div className="w-70px d-flex justify-content-center align-items-center px-2">
                <div className="cm-checkbox" title={t('CUSTOM_MODULE.SYSTEM_CANVAS.FREEZE_COLUMN')}>
                  <NewCheckbox className="scale12" checked={!!col.isFrozen} disabled={!col.isFrozen && isFrozenLimitReached} onChange={(e) => updateCol(colIdx, { isFrozen: e.target.checked })} id={`freeze_${subIndex}_${colIdx}`} />
                  <label
                    htmlFor={`freeze_${subIndex}_${colIdx}`}
                    className={`chklabel ${!col.isFrozen && isFrozenLimitReached ? '' : 'cursor-pointer'}`}
                    title={
                      !col.isFrozen && isFrozenLimitReached
                        ? t('CUSTOM_MODULE.VALIDATION.MAX_FROZEN_COLUMNS_LIMIT')
                        : t('CUSTOM_MODULE.SYSTEM_CANVAS.FREEZE_COLUMN')
                    }
                  >
                    <span className="sr-only">{t('CUSTOM_MODULE.SYSTEM_CANVAS.FREEZE_COLUMN')}</span>
                  </label>
                </div>
              </div>
              <div className="w-50px d-flex justify-content-center align-items-center gap-2">
                <span
                  className="cursor-pointer"
                  onClick={() => setEditingTablePath(editingTablePath?.colIndex === colIdx ? null : { subIndex, colIndex: colIdx })}
                  title={tCommon('COMMON_LABEL.EDIT')}
                >
                  {editingTablePath?.colIndex === colIdx ? <CloseIcon size={24} /> : <EditIcon size={24} />}
                </span>
                <span
                  className="cursor-pointer"
                  onClick={() => updateCol(colIdx, { isVisible: !(col.isVisible !== false) })}
                  title={col.isVisible !== false ? t('COMMON.HIDE') : t('COMMON.SHOW')}
                >
                  {col.isVisible !== false ? <ShowIcon size={24} /> : <HideIcon size={24} />}
                </span>
                <span title={tCommon('COMMON_LABEL.DELETE')}><DeleteIcon size={24} className="cursor-pointer" onClick={() => requestRemoveCol(colIdx)} /></span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <ConfirmPopup
        onOpen={showDeleteConfirm}
        title="Delete Column"
        message="Are you sure you want to delete this column?"
        toggle={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      {editingTablePath &&
        editingTablePath.subIndex === subIndex &&
        (() => {
          const col = table.columns[editingTablePath.colIndex];
          if (!col) return null;
          const item = toFieldItem(col, editingTablePath.colIndex);
          if (!item) return null;
          return (
            <FieldSetupCard
              fieldType={item.type}
              title={col.title ?? ''}
              onTitleChange={(title) => updateCol(editingTablePath.colIndex, { title })}
              onClose={() => setEditingTablePath(null)}
              access={getFieldSetupAccess(col as CMField, item)}
              onAccessChange={(patch) =>
                updateCol(editingTablePath.colIndex, applyConfigToField(col as CMField, { ...(item.config as Record<string, unknown>), ...patch }))
              }
            >
              {renderColumnConfig(item, col as CMField, editingTablePath.colIndex)}
            </FieldSetupCard>
          );
        })()}
    </div>
  );
};

export default memo(TableColumnsEditor);
